import { Fragment } from 'react'
import { MessagesStorageStorageFileType } from '@/util/enums'
import { humanFileSize } from '@/util/humanFileSize'
import { IconPaperclipOutline } from '@icons/IconPaperclipOutline'
import { ColumnDef } from '@tanstack/react-table'
import clsx from 'clsx'
import { SortingHeaderButton } from '../../../../components/ui/DataTable/SortingHeaderButton'
import { RowActionButtons } from './RowActionButtons'
import { TooltipButton } from '@/components/ui/TooltipButton'
import {
  getVisibleWorkspaceProperties,
  isWorkspaceDirectory,
  MessagesStorageStorageDetailItemWorkspace,
} from './workspaceFilesTableUtils'

export const workspaceFilesColumns: ColumnDef<MessagesStorageStorageDetailItemWorkspace>[] = [
  {
    id: 'filename',
    // accessorKey: 'filename',
    accessorFn: (row) => `${row.filename} ${row.blobName}`,
    header: ({ column }) => <SortingHeaderButton column={column}>Filename</SortingHeaderButton>,
    cell: ({ row }) => (
      <div className="flex flex-row items-center gap-1.5 pr-8">
        <div
          className={clsx(
            'flex size-10 shrink-0 items-center justify-center rounded-full',
            row.original.type === MessagesStorageStorageFileType.Bin ? 'bg-muted' : 'bg-primary/5'
          )}
        >
          <IconPaperclipOutline
            className={clsx(
              'size-5',
              row.original.type === MessagesStorageStorageFileType.Bin ? 'text-muted-foreground' : 'text-primary'
            )}
          />
        </div>
        <div className="flex w-full flex-col px-2 py-0">
          <TooltipButton side="top" tooltip={row.original.filename}>
            <span className="truncate text-base">{row.original.filename}</span>
          </TooltipButton>
          <TooltipButton side="bottom" tooltip={row.original.blobName}>
            <span className="truncate text-xs text-muted-foreground">{row.original.blobName}</span>
          </TooltipButton>
        </div>
      </div>
    ),
    sortingFn: (rowA, rowB) => rowA.original.filename.localeCompare(rowB.original.filename),
    filterFn: (row, _columnId, showDirectories: boolean) => showDirectories || !isWorkspaceDirectory(row.original),
    enableGlobalFilter: true,
  },
  {
    id: 'size',
    accessorKey: 'size',
    header: ({ column }) => <SortingHeaderButton column={column}>Size</SortingHeaderButton>,
    cell: ({ cell }) => humanFileSize(cell.getValue() as number),
    sortingFn: (rowA, rowB) => rowA.original.size - rowB.original.size,
    enableGlobalFilter: true,
    size: 50,
  },
  {
    id: 'properties',
    accessorFn: (row) => getVisibleWorkspaceProperties(row).map(([, value]) => value).join(', '),
    header: 'Properties',
    cell: ({ row }) => {
      const visibleProperties = getVisibleWorkspaceProperties(row.original)

      if (visibleProperties.length === 0) {
        return <span className="text-muted-foreground">-</span>
      }

      return (
        <div className="grid grid-cols-[auto_1fr] items-center gap-x-1 gap-y-2 rounded-lg py-2 pl-2 leading-4">
          <>
            {visibleProperties.map(([key, value]) => (
              <Fragment key={key}>
                <div className="flex items-center justify-end gap-1">
                  <div
                    className={clsx(
                      'rounded-lg bg-secondary/10 px-1.5 py-0.5 shadow'
                      // index === 0 ? 'bg-accent/10' : 'bg-secondary/10'
                    )}
                  >
                    {key}
                  </div>
                  -
                </div>
                <div className="text-muted-foreground">{value}</div>
              </Fragment>
            ))}
          </>
        </div>
      )
    },
    enableGlobalFilter: true,
  },
  {
    id: 'actions',
    cell: ({ row }) => <RowActionButtons item={row.original} />,
    enableGlobalFilter: false,
    size: 50,
  },
  // {
  //   id: 'templateFileCount',
  //   header: 'Template files',
  //   cell: ({ row }) => {
  //     return (
  //       <SheetWithFormComponent
  //         wide
  //         title={`${row.original.name} ► Manage template file uploads`}
  //         trigger={
  //           <button className="group flex items-center gap-x-2 p-1.5">
  //             <IconCopyOutline className="size-5 group-hover:text-primary" />
  //             <NumberBadge value={row.original.templateFileCount ?? 0} />
  //             <IconOpenInNewOutline className="ml-5 hidden size-4 shrink-0 self-center text-primary group-hover:block" />
  //           </button>
  //         }
  //       >
  //         <TemplatesFilesList selectedEntity={row.original} />
  //       </SheetWithFormComponent>
  //     )
  //   },
  // },
  // {
  //   id: 'dataAssetsFileCount',
  //   header: 'Data asset files',
  //   cell: ({ row }) => {
  //     return (
  //       <SheetWithFormComponent
  //         wide
  //         title={`${row.original.name} ► Manage data asset files`}
  //         trigger={
  //           <button className="group flex items-center gap-x-2 p-1.5">
  //             <IconBoxOutline className="size-5 group-hover:text-primary" />
  //             <NumberBadge value={row.original.dataAssetFileCount ?? 0} />
  //             <IconOpenInNewOutline className="ml-5 hidden size-4 shrink-0 self-center text-primary group-hover:block" />
  //           </button>
  //         }
  //       >
  //         <DataAssetsFilesList selectedEntity={row.original} />
  //       </SheetWithFormComponent>
  //     )
  //   },
  // },
  // {
  //   id: 'actions',
  //   // header: 'Actions',
  //   cell: ({ row }) => (
  //     <div className="flex justify-end pr-5">
  //       <RowActionsMenu entity={row.original} />
  //     </div>
  //   ),
  // },
  // {
  //   id: 'description',
  //   accessorFn: (row) => row.description.value,
  //   header: 'Role description',
  //   cell: ({ cell }) => cell.getValue() as string,
  //   enableGlobalFilter: true,
  // },
  // {
  //   id: 'memberCount',
  //   accessorFn: (row) => row.memberCount.value.toString(),
  //   header: 'Member count',
  //   cell: ({ row }) => <NumberBadge value={row.original.memberCount.value} />,
  //   enableGlobalFilter: true,
  // },
  // {
  //   id: 'teamCount',
  //   accessorFn: (row) => row.teamCount.value.toString(),
  //   header: 'Team count',
  //   cell: ({ row }) => <NumberBadge value={row.original.teamCount.value} />,
  //   enableGlobalFilter: true,
  // },
  // {
  //   id: 'actions',
  //   // header: 'Actions',
  //   enableGlobalFilter: false, // Don't include actions in search
  //   cell: ({ row }) => (
  //     <div className="flex justify-end">
  //       <ApplicationRoleForm
  //         sheetTitle="Edit application role"
  //         selectedAppRole={row.original}
  //         sheetTrigger={
  //           <Button size="icon" variant="ghost">
  //             <IconEditOutline className="size-4" />
  //           </Button>
  //         }
  //       />
  //     </div>
  //   ),
  // },
]
